import Collapsible from '../shared/Collapsible.js';
import './poultryDesign.css';
import './poultryFeedRedesign.css';
import {useAppNavigate} from '../app/navigation/index.js';
import Button from '../shared/Button.js';
// ============================================================================
// src/broiler/BroilerFeedView.tsx
// ----------------------------------------------------------------------------
// Minimal poultry feed ledger — same shape as PigFeedView, three feed types.
//
// Top of screen:
//   • One equation strip per feed type: On Hand + Ordered − Need = Balance.
//     Order fields edit in place; the result is tinted for coverage or shortage.
//   • Update On Hand sits in the card footer with feed-type pills, an lb input,
//     a delivery-included switch and status from the confirmed autosave.
//   • Compact Feed by Batch and Feed by Flock rows open the existing records;
//     completed batches fold into the list footer. The monthly ledger remains
//     available as a collapsed read-only reference.
//
// Saved-vs-active rule:
//   A month is "fully saved" only when starter, grower, AND layerfeed
//   orders are all present (including explicit 0). Active month is the
//   current calendar month and does not advance early when saved.
//
// Save behavior (four confirmed autosaves, no save button):
//   • Each typed feed type autosaves itself 1500 ms after the last keystroke
//     as one poultry-map patch {starter|grower|layerfeed}[activeYM]; blanks
//     are never written and never block a sibling's save. The active month
//     stays pinned until the calendar month flips.
//   • The only explicit order action is "Accept 0" — the recorded zero
//     decision for a wholly fresh month whose three recommendations are all
//     exactly 0 (no drafts, no persisted rows). A typed 0 autosaves like any
//     other value.
//   • The physical count autosaves the same way, dated the day the revision
//     was typed; a delivery-toggle change on an untouched count is held
//     incomplete rather than re-dating an older count.
//
// Math source-of-truth:
//   Poultry burn flows through poultryDailyBurnLbs (which internally
//   uses getFeedSchedule + LAYER_FEED_SCHEDULE + LAYER_FEED_PER_DAY +
//   computeProjectedCount) and the existing monthly helpers
//   calcBatchFeedForMonth + calcLayerFeedForMonth. No duplicated feed-rate
//   constants. Physical count is ground truth per feed type and avoids
//   double-counting the count-month order when the operator's checkbox
//   says the count already absorbed it.
// ============================================================================
import React, {useEffect, useRef, useState} from 'react';
import {fmtDate as fmt, todayISO} from '../lib/dateUtils.js';
import {openableProps} from '../shared/openable.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
import {
  calcBatchFeedForMonth,
  calcLayerFeedForMonth,
  calcBatchFeed,
  calcBroilerStatsFromDailys,
  calcPoultryStatus,
  getBatchDisplayColor,
  breedLabel,
  LAYER_FEED_SCHEDULE,
  LAYER_FEED_PER_DAY,
} from '../lib/broiler.js';
import type {BroilerBatch, LayerBatchRow, LayerHousingRow as BroilerLayerHousingRow} from '../lib/broiler.js';
import {computeProjectedCount} from '../lib/layerHousing.js';
import type {LayerDailyRow, LayerHousingRow} from '../lib/layerHousing.js';
import {poultryDailyBurnLbs} from '../lib/feedPlanner.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {BroilerFeedCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import {
  calendarOrderYM,
  feedOrderBasis,
  recommendedFeedOrder,
  remainingNeedThruNext,
  thruNextDelta,
} from '../lib/feedOrderBasis.js';
import Header from '../shared/Header.tsx';
import {sb} from '../lib/supabase.js';
import {PROFILE_STATUS, useAuth} from '../contexts/AuthContext.jsx';
import {
  ackedBroilerFeedInventory,
  ackedBroilerOrderMap,
  broilerCountDraftProblem,
  broilerFeedOwner,
  broilerOrderDraftProblem,
  makeBroilerCountPersist,
  makeBroilerCountSender,
  makeBroilerOrderPersist,
  makeBroilerOrderSender,
  SETTLING_REJECTIONS_COUNT,
  SETTLING_REJECTIONS_ORDER,
  useBroilerFeedAutosave,
} from './broilerFeedAutosave.js';
import type {
  BroilerCountAck,
  BroilerOrderAcks,
  BroilerOrderDraft,
  BroilerCountDraft,
  LiveIdentity,
} from './broilerFeedAutosave.js';

// The three feed types this ledger walks, and the three keys the persisted
// ppp-feed-orders-v1 blob uses for them. They differ in exactly one place —
// 'layer' is stored as 'layerfeed' — which is why the code carries both and why
// both are spelled out rather than derived.
type FeedTypeKey = 'starter' | 'grower' | 'layer';
type FeedOrderKey = 'starter' | 'grower' | 'layerfeed';
type OrderDrafts = Record<FeedOrderKey, string>;

// What `feedOrders` is before the resource settles or when its row is absent.
// The four maps preserve the resource's documented shape for local rendering;
// the save path sends only the three poultry maps as a patch, never this whole
// object, so an empty fallback cannot erase the shared pig map.
const NO_FEED_ORDERS: Record<string, Record<string, unknown>> = Object.freeze({
  pig: {},
  starter: {},
  grower: {},
  layerfeed: {},
});

// A dailys row as the ledger reads it. Broiler (poultry_dailys) and layer
// (layer_dailys) rows are concatenated and walked through the SAME three
// columns, so one record covers both; LayerDailyRow already declares `date` and
// carries the open index signature these raw table rows need.
type FeedDailyRow = LayerDailyRow & {
  feed_type?: string | null;
  feed_lbs?: number | string | null;
  grit_lbs?: number | string | null;
};

// The batch/housing rows this view forwards to the shared helpers. Each one is
// the INTERSECTION of what those helpers declare, so a row satisfies every
// callee without any of them being re-described here: broiler.js and
// layerHousing.js each own a LayerHousingRow, and computeProjectedCount and
// calcLayerFeedForMonth want one each.
type BroilerBatchRecord = BroilerBatch & {
  name: string;
  brooder?: string | number | null;
  schooner?: string | number | null;
  totalToProcessor?: number | string | null;
};
type LayerBatchRecord = LayerBatchRow & {name?: string | null; status?: string | null};
type LayerHousingRecord = BroilerLayerHousingRow & LayerHousingRow;

// ppp-poultry-feed-inventory-v1: one physical count per feed type. Written by
// the count autosave below, which is the only writer, so the shape is this
// file's own rather than something inferred from the blob.
type PoultryFeedCount = {count: number; date: string; includesCurrentMonthDelivery: boolean};
type PoultryFeedInventoryMap = Record<string, PoultryFeedCount | undefined>;

// One month's Start − Consumed + Ordered = End row, per feed type. `rawOrdered`
// is written only on the physical-count month.
type LedgerEntry = {
  start: number;
  consumed: number;
  actualCons: number;
  projCons: number;
  ordered: number;
  rawOrdered?: number;
  end: number;
  countMonth: boolean;
  countAdj: number | null;
};

// This released surface has no App prop channel. `broiler.feed.core` owns its
// seven render reads; a save-time feed-order patch preserves the shared pig map,
// both writes go through the migration-249 audited operation (see
// broilerFeedAutosave), and the collapse set is
// local-only UI state. Do not reintroduce a domain-entry hand-off or provider
// hook: either would make this surface consume legacy boot again.
const BROILER_FEED_PARAMS = Object.freeze({});

export default function BroilerFeedView() {
  const navigate = useAppNavigate();
  const feedResource = useRouteResource(RESOURCE.BROILER_FEED_CORE, BROILER_FEED_PARAMS);
  const feedData = feedResource.data as BroilerFeedCoreData | null;
  // WHO the drafts belong to. Every autosave revision freezes the account,
  // effective role, preview role and session epoch it was typed under; a new
  // owner gets new controllers, and an old owner's late reply publishes
  // nothing. The keyed saveStatus store feeds the header chip the shared
  // Header already renders; the unscoped legacy sink is never driven.
  const {authState, rolePreview, profileStatus, sessionEpoch} = useAuth();
  const owner = broilerFeedOwner(authState, rolePreview, profileStatus === PROFILE_STATUS.RESOLVED, sessionEpoch);
  const ownerKey = owner ? owner.key : '';
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  // The FULL live writer identity, as the SDK session proves it: the subject
  // comes from sb.auth.getSession and must match the mount's resolved owner,
  // whose complete identity (role/preview/epoch) is re-checked AFTER the
  // await. Controllers bind the owner they were created for and compare this
  // live identity against that binding before every RPC.
  async function readLiveIdentity(): Promise<LiveIdentity | null> {
    const expected = ownerRef.current;
    if (!expected || !aliveRef.current) return null;
    try {
      const {data} = await sb.auth.getSession();
      const subject = data.session?.user?.id;
      const current = ownerRef.current;
      if (!current || !aliveRef.current || current.key !== expected.key) return null;
      if (!subject || subject !== current.subjectId) return null;
      return {
        subjectId: subject,
        effectiveRole: current.effectiveRole,
        previewRole: current.previewRole,
        sessionEpoch: current.sessionEpoch,
      };
    } catch {
      return null;
    }
  }
  // The server's confirmed revisions, per owner. A route resource cannot show
  // a write it has not refetched, so each confirmed ack is laid over the
  // route's own maps — and only while the acking owner is still the one
  // rendering, so a stale account's confirmation can never repaint a newer
  // session. An unconfirmed or failed write acks nothing.
  const [orderAcks, setOrderAcks] = useState<{
    starter: BroilerOrderAcks | null;
    grower: BroilerOrderAcks | null;
    layerfeed: BroilerOrderAcks | null;
  }>({starter: null, grower: null, layerfeed: null});
  const [countAck, setCountAck] = useState<BroilerCountAck | null>(null);
  // The current owner's latest failure per writer, kept until a corrected
  // revision or a confirmed save clears it.
  const [orderFailure, setOrderFailure] = useState<{
    starter: {ownerKey: string; message: string} | null;
    grower: {ownerKey: string; message: string} | null;
    layerfeed: {ownerKey: string; message: string} | null;
  }>({starter: null, grower: null, layerfeed: null});
  const [countFailure, setCountFailure] = useState<{ownerKey: string; message: string} | null>(null);
  // Completed-row visibility is local presentation state.
  const [showCompleted, setShowCompleted] = useState(false);
  // The resource hands these rows out as `unknown[]` for the same reason the
  // three contexts did. The narrowing happens here, once, at the boundary that
  // knows which columns this view reads.
  const batches = (feedData?.batches || []) as BroilerBatchRecord[];
  const layerBatches = (feedData?.layerBatches || []) as LayerBatchRecord[];
  const layerHousings = (feedData?.layerHousings || []) as LayerHousingRecord[];
  const allLayerDailys = (feedData?.allLayerDailys || []) as FeedDailyRow[];
  const broilerDailys = (feedData?.broilerDailys || []) as FeedDailyRow[];
  // The route's blob plus the current owner's confirmed acks. An absent row is
  // the four-map seed, not `{}` for orders; the count map may legitimately be
  // empty until the first count is taken.
  const routeFeedOrders = (feedData?.feedOrders ?? NO_FEED_ORDERS) as Record<string, Record<string, unknown>>;
  const feedOrders = {
    ...routeFeedOrders,
    starter: ackedBroilerOrderMap(routeFeedOrders.starter || {}, orderAcks.starter, ownerKey),
    grower: ackedBroilerOrderMap(routeFeedOrders.grower || {}, orderAcks.grower, ownerKey),
    layerfeed: ackedBroilerOrderMap(routeFeedOrders.layerfeed || {}, orderAcks.layerfeed, ownerKey),
  };
  const poultryFeedInventory = ackedBroilerFeedInventory(
    (feedData?.poultryFeedInventory ?? {}) as PoultryFeedInventoryMap,
    countAck,
    ownerKey,
  );
  const [countType, setCountType] = useState('starter');
  const [editingMonthYM, setEditingMonthYM] = useState<string | null>(null);
  const [activeOrderDrafts, setActiveOrderDrafts] = useState<OrderDrafts>({starter: '', grower: '', layerfeed: ''});

  const today = new Date();
  const todayDate = todayISO();

  function addMonthsYM(ym: string, delta: number) {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
  }
  function ymLabel(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', {month: 'short', year: 'numeric'});
  }
  function ymShort(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', {month: 'short'});
  }

  // ── Calendar window ──────────────────────────────────────────────────────
  const months: string[] = [];
  for (let i = -12; i <= 6; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    months.push(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'));
  }
  const thisYM = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0');

  const activeBroilers = batches.filter((b) => b.hatchDate);
  const activeLayerBatchesForFeed = (layerBatches || []).filter(
    (b) => b.status === 'active' && b.name !== 'Retirement Home',
  );
  const activeHousings = (layerHousings || []).filter((h) => h.status === 'active');

  // ── Actual consumption by month + feed type ───────────────────────────────
  const actualByMonth: {[yearMonth: string]: {starter: number; grower: number; layer: number}} = {};
  months.forEach((ym) => {
    actualByMonth[ym] = {starter: 0, grower: 0, layer: 0};
  });
  (broilerDailys || []).forEach((d) => {
    if (!d.date) return;
    const ym = d.date.substring(0, 7);
    if (!actualByMonth[ym]) return;
    const lbs = parseFloat(String(d.feed_lbs)) || 0;
    if (d.feed_type === 'STARTER') actualByMonth[ym].starter += lbs;
    else if (d.feed_type === 'GROWER') actualByMonth[ym].grower += lbs;
  });
  (allLayerDailys || []).forEach((d) => {
    if (!d.date) return;
    const ym = d.date.substring(0, 7);
    if (!actualByMonth[ym]) return;
    const lbs = parseFloat(String(d.feed_lbs)) || 0;
    if (d.feed_type === 'STARTER') actualByMonth[ym].starter += lbs;
    else if (d.feed_type === 'GROWER') actualByMonth[ym].grower += lbs;
    else if (d.feed_type === 'LAYER') actualByMonth[ym].layer += lbs;
  });

  // Per-month per-type projected totals via existing schedule helpers.
  const monthlyData = months.map((ym) => {
    const p = ym.split('-').map(Number);
    const daysInMonth = new Date(p[0], p[1], 0).getDate();
    const isFuture = ym > thisYM;
    const isCurrent = ym === thisYM;
    let bStarter = 0;
    let bGrover = 0;
    activeBroilers.forEach((b) => {
      const f = calcBatchFeedForMonth(b, ym);
      bStarter += f.starter;
      bGrover += f.grower;
    });
    let lStarter = 0;
    let lGrover = 0;
    let lLayer = 0;
    activeLayerBatchesForFeed.forEach((b) => {
      const f = calcLayerFeedForMonth(b, layerHousings || [], allLayerDailys || [], ym);
      lStarter += f.starter;
      lGrover += f.grower;
      lLayer += f.layer;
    });
    const starter = Math.round(bStarter + lStarter);
    const grower = Math.round(bGrover + lGrover);
    const layerFeed = Math.round(lLayer);
    const act = actualByMonth[ym] || {starter: 0, grower: 0, layer: 0};
    const ordS = (feedOrders.starter || {})[ym];
    const ordG = (feedOrders.grower || {})[ym];
    const ordL = (feedOrders.layerfeed || {})[ym];
    return {
      ym,
      daysInMonth,
      isFuture,
      isCurrent,
      starter,
      grower,
      layerFeed,
      actualStarter: Math.round(act.starter),
      actualGrover: Math.round(act.grower),
      actualLayer: Math.round(act.layer),
      ordS: ordS != null ? ordS : null,
      ordG: ordG != null ? ordG : null,
      ordL: ordL != null ? ordL : null,
    };
  });

  // ── Persistence ──────────────────────────────────────────────────────────
  // Four confirmed autosaves (1500 ms): one per poultry order field plus the
  // physical count. Each is the shared confirmedDraftAutosave controller with
  // the owner-scoped wiring from broilerFeedAutosave: the revision's own
  // request id, a held same-id replay for anything the server did not
  // definitively answer, and the keyed saveStatus store. Ordinary valid edits
  // save themselves; nothing writes from mount, refetch, rollover, type
  // switch, or a recommendation alone.
  const editingMonthYMRef = useRef<string | null>(null);
  editingMonthYMRef.current = editingMonthYM;
  const confirmedIncludesFor = (type: string) => {
    const entry = poultryFeedInventory && (poultryFeedInventory as PoultryFeedInventoryMap)[type];
    return !!(entry && entry.includesCurrentMonthDelivery);
  };
  function orderValidate(key: FeedOrderKey, draft: BroilerOrderDraft) {
    return broilerOrderDraftProblem(draft, ownerRef.current, persistedValueForActive(key)) === null;
  }
  const starterAutosave = useBroilerFeedAutosave<BroilerOrderDraft, {ym: string; orderForMonth: number}>({
    guardKey: 'broiler-feed-order-starter',
    ownerKey,
    requestPrefix: 'broiler-order-starter',
    debounceMs: 1500,
    validate: (draft) => orderValidate('starter', draft),
    persist: makeBroilerOrderPersist('starter', {
      getCurrentOwner: () => ownerRef.current,
      isAlive: () => aliveRef.current,
      onFailure: (ownerKeyAtDraft, message) =>
        setOrderFailure((prev) => ({...prev, starter: {ownerKey: ownerKeyAtDraft, message}})),
      onClearFailure: () => setOrderFailure((prev) => ({...prev, starter: null})),
      onAck: (ownerKeyAtDraft, ym, value) =>
        setOrderAcks((prev) => ({
          ...prev,
          starter: {
            ownerKey: ownerKeyAtDraft,
            months: {
              ...(prev.starter && prev.starter.ownerKey === ownerKeyAtDraft ? prev.starter.months : {}),
              [ym]: value,
            },
          },
        })),
    }),
    makeSend: (fence) => makeBroilerOrderSender(sb, 'starter', fence),
    owner,
    readLiveIdentity,
    settlingRejections: SETTLING_REJECTIONS_ORDER,
  });
  const growerAutosave = useBroilerFeedAutosave<BroilerOrderDraft, {ym: string; orderForMonth: number}>({
    guardKey: 'broiler-feed-order-grower',
    ownerKey,
    requestPrefix: 'broiler-order-grower',
    debounceMs: 1500,
    validate: (draft) => orderValidate('grower', draft),
    persist: makeBroilerOrderPersist('grower', {
      getCurrentOwner: () => ownerRef.current,
      isAlive: () => aliveRef.current,
      onFailure: (ownerKeyAtDraft, message) =>
        setOrderFailure((prev) => ({...prev, grower: {ownerKey: ownerKeyAtDraft, message}})),
      onClearFailure: () => setOrderFailure((prev) => ({...prev, grower: null})),
      onAck: (ownerKeyAtDraft, ym, value) =>
        setOrderAcks((prev) => ({
          ...prev,
          grower: {
            ownerKey: ownerKeyAtDraft,
            months: {
              ...(prev.grower && prev.grower.ownerKey === ownerKeyAtDraft ? prev.grower.months : {}),
              [ym]: value,
            },
          },
        })),
    }),
    makeSend: (fence) => makeBroilerOrderSender(sb, 'grower', fence),
    owner,
    readLiveIdentity,
    settlingRejections: SETTLING_REJECTIONS_ORDER,
  });
  const layerfeedAutosave = useBroilerFeedAutosave<BroilerOrderDraft, {ym: string; orderForMonth: number}>({
    guardKey: 'broiler-feed-order-layerfeed',
    ownerKey,
    requestPrefix: 'broiler-order-layerfeed',
    debounceMs: 1500,
    validate: (draft) => orderValidate('layerfeed', draft),
    persist: makeBroilerOrderPersist('layerfeed', {
      getCurrentOwner: () => ownerRef.current,
      isAlive: () => aliveRef.current,
      onFailure: (ownerKeyAtDraft, message) =>
        setOrderFailure((prev) => ({...prev, layerfeed: {ownerKey: ownerKeyAtDraft, message}})),
      onClearFailure: () => setOrderFailure((prev) => ({...prev, layerfeed: null})),
      onAck: (ownerKeyAtDraft, ym, value) =>
        setOrderAcks((prev) => ({
          ...prev,
          layerfeed: {
            ownerKey: ownerKeyAtDraft,
            months: {
              ...(prev.layerfeed && prev.layerfeed.ownerKey === ownerKeyAtDraft ? prev.layerfeed.months : {}),
              [ym]: value,
            },
          },
        })),
    }),
    makeSend: (fence) => makeBroilerOrderSender(sb, 'layerfeed', fence),
    owner,
    readLiveIdentity,
    settlingRejections: SETTLING_REJECTIONS_ORDER,
  });
  const orderAutosaveByOrderKey: Record<FeedOrderKey, typeof starterAutosave> = {
    starter: starterAutosave,
    grower: growerAutosave,
    layerfeed: layerfeedAutosave,
  };
  const countAutosave = useBroilerFeedAutosave<
    BroilerCountDraft,
    {
      type: string;
      count: number;
      date: string;
      includesCurrentMonthDelivery: boolean;
    }
  >({
    guardKey: 'broiler-feed-count',
    ownerKey,
    requestPrefix: 'broiler-inv',
    debounceMs: 1500,
    validate: (draft) => broilerCountDraftProblem(draft, ownerRef.current, confirmedIncludesFor(draft.type)) === null,
    persist: makeBroilerCountPersist({
      getCurrentOwner: () => ownerRef.current,
      isAlive: () => aliveRef.current,
      onFailure: (ownerKeyAtDraft, message) => setCountFailure({ownerKey: ownerKeyAtDraft, message}),
      onClearFailure: () => setCountFailure(null),
      onAck: (ownerKeyAtDraft, entry) => setCountAck({ownerKey: ownerKeyAtDraft, entry}),
    }),
    makeSend: (fence) => makeBroilerCountSender(sb, fence),
    owner,
    readLiveIdentity,
    settlingRejections: SETTLING_REJECTIONS_COUNT,
  });

  // ── Active-month resolution ──────────────────────────────────────────────
  // A month is "fully saved" only when all three feed-type orders are
  // present (including explicit 0). Active month = the first month at or
  // after today's month that is NOT fully saved.
  function isMonthFullySaved(ym: string) {
    return (
      (feedOrders.starter || {})[ym] != null &&
      (feedOrders.grower || {})[ym] != null &&
      (feedOrders.layerfeed || {})[ym] != null
    );
  }
  const autoActiveYM = calendarOrderYM(today);
  const activeYM = editingMonthYM != null ? editingMonthYM : autoActiveYM;
  const isActiveEditMode = editingMonthYM != null;
  const prevYM = addMonthsYM(activeYM, -1);
  const nextYM = addMonthsYM(activeYM, 1);
  const prevLabel = ymShort(prevYM);
  const activeLabel = ymShort(activeYM);
  const nextLabel = ymShort(nextYM);

  // One visible order card only. Saving the pinned month never advances this
  // section; it moves when the calendar month moves.

  // ── Running ledger per feed type ─────────────────────────────────────────
  // Anchored by the per-type physical count when present; otherwise by
  // the first month where that type has a saved order. Walks forward
  // through all months in the window so prev/active/next all have a
  // valid Start/Consumed/Ordered/End triple.
  const allDailys = (broilerDailys || []).concat(allLayerDailys || []);
  const pDaysLeft = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - today.getDate();

  // Rest-of-current-month burn per feed type via feedPlanner. Sums daily
  // burn for every remaining day in the current month so the ledger's
  // pRem/pRoj values respect each batch's age-by-day instead of a flat
  // proportional split.
  const currentMonthRemainingBurnByType = (() => {
    if (pDaysLeft <= 0) return {starter: 0, grower: 0, layer: 0};
    let starter = 0;
    let grower = 0;
    let layer = 0;
    for (let i = 1; i <= pDaysLeft; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const iso =
        d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      const out = poultryDailyBurnLbs(iso, {
        batches: activeBroilers,
        layerBatches: activeLayerBatchesForFeed,
        layerHousings: layerHousings || [],
        layerDailys: allLayerDailys || [],
      });
      starter += out.starterLbs;
      grower += out.growerLbs;
      layer += out.layerLbs;
    }
    return {starter: Math.round(starter), grower: Math.round(grower), layer: Math.round(layer)};
  })();

  const pLedger: Record<FeedTypeKey, {[yearMonth: string]: LedgerEntry | null}> = {starter: {}, grower: {}, layer: {}};
  (['starter', 'grower', 'layer'] as FeedTypeKey[]).forEach((type) => {
    const orderKey: FeedOrderKey = type === 'layer' ? 'layerfeed' : type;
    const ftKey = type === 'starter' ? 'STARTER' : type === 'grower' ? 'GROWER' : 'LAYER';
    const projKey = type === 'starter' ? 'starter' : type === 'grower' ? 'grower' : 'layerFeed';
    const actualKey = type === 'starter' ? 'actualStarter' : type === 'grower' ? 'actualGrover' : 'actualLayer';
    const orderedKey = type === 'starter' ? 'ordS' : type === 'grower' ? 'ordG' : 'ordL';
    const typeOrderMonths = Object.keys(feedOrders[orderKey] || {})
      .filter((k) => (parseFloat(String((feedOrders[orderKey] || {})[k])) || 0) > 0)
      .sort();
    const firstOrderYM = typeOrderMonths.length > 0 ? typeOrderMonths[0] : '9999-99';
    const inv = poultryFeedInventory && poultryFeedInventory[type];
    const invYM = inv && inv.date ? inv.date.substring(0, 7) : null;
    let runBal = 0;
    let countApplied = false;
    const sorted = monthlyData.slice().sort((a, b) => a.ym.localeCompare(b.ym));
    for (let i = 0; i < sorted.length; i++) {
      const md = sorted[i];
      if (md.ym < firstOrderYM && (!invYM || md.ym < invYM)) {
        pLedger[type][md.ym] = null;
        continue;
      }
      let lgStart = runBal;
      let isCountMonth = false;
      let countAdj: number | null = null;
      if (inv && invYM && !countApplied) {
        if (invYM === md.ym) {
          const thisMonthOrderForAdj = parseFloat(String(md[orderedKey])) || 0;
          const systemEstThisMonth = runBal + (inv.includesCurrentMonthDelivery ? thisMonthOrderForAdj : 0);
          countAdj = Math.round(inv.count - systemEstThisMonth);
          lgStart = inv.count;
          isCountMonth = true;
          countApplied = true;
          let cAfter = 0;
          allDailys.forEach((d) => {
            if (d.date && d.date > inv.date && d.date.startsWith(md.ym) && d.feed_type === ftKey)
              cAfter += parseFloat(String(d.feed_lbs)) || 0;
          });
          let pRem = 0;
          if (md.isCurrent && pDaysLeft > 0) pRem = currentMonthRemainingBurnByType[type];
          const cons = Math.round(cAfter + pRem);
          const ord = inv.includesCurrentMonthDelivery ? 0 : thisMonthOrderForAdj;
          const en = Math.round(lgStart - cons + ord);
          pLedger[type][md.ym] = {
            start: lgStart,
            consumed: cons,
            actualCons: Math.round(cAfter),
            projCons: Math.round(pRem),
            ordered: ord,
            rawOrdered: thisMonthOrderForAdj,
            end: en,
            countMonth: true,
            countAdj,
          };
          runBal = en;
          continue;
        } else if (invYM < md.ym) {
          lgStart = inv.count;
          countApplied = true;
        }
      }
      let aCtual = md[actualKey];
      let pRoj = 0;
      if (md.isCurrent && pDaysLeft > 0) pRoj = currentMonthRemainingBurnByType[type];
      else if (md.isFuture) {
        pRoj = md[projKey];
        aCtual = 0;
      }
      const cons2 = Math.round(aCtual + pRoj);
      const ord2 = parseFloat(String(md[orderedKey])) || 0;
      const en2 = Math.round(lgStart - cons2 + ord2);
      pLedger[type][md.ym] = {
        start: Math.round(lgStart),
        consumed: cons2,
        actualCons: Math.round(aCtual),
        projCons: Math.round(pRoj),
        ordered: ord2,
        end: en2,
        countMonth: isCountMonth,
        countAdj,
      };
      runBal = en2;
    }
  });

  // ── Top-tile derived numbers ─────────────────────────────────────────────
  const TYPE_KEYS: FeedTypeKey[] = ['starter', 'grower', 'layer'];
  // Actual On Hand per type — count + arrived-after-count − consumed-since-count.
  // Each of these per-type maps is declared with an index signature rather than
  // a complete Record, because every one of them is built by the forEach that
  // immediately follows its `{}` initializer.
  const actualOnHand: {[feedType: string]: number | null} = {};
  TYPE_KEYS.forEach((type) => {
    const orderKey = type === 'layer' ? 'layerfeed' : type;
    const inv = poultryFeedInventory && poultryFeedInventory[type];
    if (!inv || !inv.date) {
      actualOnHand[type] = null;
      return;
    }
    const invYM = inv.date.substring(0, 7);
    const ftKey = type === 'starter' ? 'STARTER' : type === 'grower' ? 'GROWER' : 'LAYER';
    const ordersArrivedAfterCount = Object.entries(feedOrders[orderKey] || {}).reduce((s, e) => {
      const ym = e[0];
      const v = parseFloat(String(e[1])) || 0;
      if (ym > invYM && ym < thisYM) return s + v;
      if (ym === invYM && !inv.includesCurrentMonthDelivery && ym < thisYM) return s + v;
      return s;
    }, 0);
    const consumedSinceCount = Math.round(
      allDailys
        .filter((d) => d.date && d.date > inv.date && d.feed_type === ftKey)
        .reduce((s, d) => s + (parseFloat(String(d.feed_lbs)) || 0), 0),
    );
    actualOnHand[type] = Math.round(inv.count + ordersArrivedAfterCount - consumedSinceCount);
  });
  const endOfPrev: {[feedType: string]: number | null} = {};
  TYPE_KEYS.forEach((type) => {
    const lg = pLedger[type][prevYM];
    endOfPrev[type] = lg ? lg.end : null;
  });
  // Order basis and dynamic Need Thru [next] per type. A current-month physical
  // count is ground truth for "on hand now" and supersedes the previous-month
  // ending ESTIMATE. Demand always deducts the active month's actual feeding;
  // a new inventory count must not reset that deduction. Since-count feeding
  // adjusts on-hand stock above, not the remaining demand.
  const basisIsCount: {[feedType: string]: boolean} = {};
  const needThruNext: {[feedType: string]: number} = {};
  const recommendedOrder: {[feedType: string]: number | null} = {};
  const thruNextDeltaByType: {[feedType: string]: number | null} = {};
  // The equation row has to ADD UP on screen, so its first column must be the
  // same `basis` thruNextDelta subtracts from — Actual On Hand where the type
  // was counted this month, otherwise the previous month's end less what the
  // active month has already consumed. Showing the raw count here would leave a
  // row reading "— + ordered − need = a number", which is not an equation.
  const orderBasisByType: {[feedType: string]: number | null | undefined} = {};
  const activeOrderedByType: {[feedType: string]: number} = {};
  const orderIncludedByType: {[feedType: string]: boolean} = {};
  TYPE_KEYS.forEach((type) => {
    const orderKey: FeedOrderKey = type === 'layer' ? 'layerfeed' : type;
    const projKey = type === 'starter' ? 'starter' : type === 'grower' ? 'grower' : 'layerFeed';
    const actualKey = type === 'starter' ? 'actualStarter' : type === 'grower' ? 'actualGrover' : 'actualLayer';
    const inv = poultryFeedInventory && poultryFeedInventory[type];
    const invYM = inv && inv.date ? inv.date.substring(0, 7) : null;
    const hasCurrentCount = invYM === thisYM && actualOnHand[type] != null;
    basisIsCount[type] = hasCurrentCount;
    const a = monthlyData.find((m) => m.ym === activeYM);
    const n = monthlyData.find((m) => m.ym === nextYM);
    const activeConsumed = a ? a[actualKey] : 0;
    needThruNext[type] = remainingNeedThruNext({
      activeProjected: a ? a[projKey] : 0,
      activeConsumed,
      nextProjected: n ? n[projKey] : 0,
    });
    recommendedOrder[type] = recommendedFeedOrder({
      needThruNext: needThruNext[type],
      hasCurrentCount,
      actualOnHand: actualOnHand[type],
      endOfPrevEst: endOfPrev[type],
      activeConsumed,
    });
    const draft = (activeOrderDrafts[orderKey] || '').trim();
    const draftN = parseFloat(draft);
    const savedOrder = (feedOrders[orderKey] || {})[activeYM];
    const activeOrdered =
      Number.isFinite(draftN) && draftN >= 0 ? draftN : isActiveEditMode ? 0 : parseFloat(String(savedOrder)) || 0;
    // Keep the recorded order total for editing/history, but add none of it
    // again when the current count already includes this same month's order.
    const orderIncluded = hasCurrentCount && invYM === activeYM && !!inv?.includesCurrentMonthDelivery;
    orderIncludedByType[type] = orderIncluded;
    orderBasisByType[type] = feedOrderBasis({
      hasCurrentCount,
      actualOnHand: actualOnHand[type],
      endOfPrevEst: endOfPrev[type],
      activeConsumed,
    });
    activeOrderedByType[type] = Math.max(0, Math.round(activeOrdered || 0));
    thruNextDeltaByType[type] = thruNextDelta({
      needThruNext: needThruNext[type],
      hasCurrentCount,
      actualOnHand: actualOnHand[type],
      endOfPrevEst: endOfPrev[type],
      activeConsumed,
      activeOrdered: orderIncluded ? 0 : activeOrdered,
    });
  });
  const anyCurrentCount = TYPE_KEYS.some((type) => basisIsCount[type]);
  const allCurrentCount = TYPE_KEYS.every((type) => basisIsCount[type]);
  // Caption must reflect the REAL per-type basis. In a mixed state (only some
  // feed types counted this month) a tile-wide "vs Actual On Hand" would
  // misrepresent the still-estimated types.
  const orderBasisCaption = allCurrentCount
    ? 'vs Actual On Hand'
    : anyCurrentCount
      ? 'vs Actual On Hand where counted; otherwise End of ' + prevLabel + ' Est.'
      : 'vs End of ' + prevLabel + ' Est.';
  // Second summary tile stays pinned to the current calendar month. The order
  // workflow can advance activeYM when the calendar month flips, but this
  // summary still shows the current month-end estimate per type. Saved order
  // only -- updates on save, not while typing an unsaved draft.
  const estTileYM = thisYM;
  const endOfCurrent: {[feedType: string]: number | null} = {};
  TYPE_KEYS.forEach((type) => {
    const lg = pLedger[type][estTileYM];
    endOfCurrent[type] = lg ? lg.end : null;
  });
  const estTileValues = endOfCurrent;
  const estTileLabel = 'End of ' + ymShort(estTileYM) + ' Est.';

  // Top tiles render three stacked per-type rows so each feed type's
  // value scans on its own. The order matches the active card's per-type
  // row order. WI-2e: feed-type words are categories, not colors → black labels.
  const TILE_TYPE_ROWS = [
    {key: 'starter', label: 'Starter'},
    {key: 'grower', label: 'Grower'},
    {key: 'layer', label: 'Layer Feed'},
  ];

  // ── Active-card live overrides for End-of-Month while typing ────────────
  // For each feed type, splice the operator's typed draft (or the
  // recommendation-zero implicit save) into the active ledger entry so
  // the equation's End updates as they type. Top tiles (incl. the count-aware
  // Est. tile) still derive from the persisted-only ledger.
  function activeDraftEnd(type: FeedTypeKey) {
    const orderKey: FeedOrderKey = type === 'layer' ? 'layerfeed' : type;
    const lg = pLedger[type][activeYM];
    if (!lg) return null;
    const draftKey = orderKey;
    const draft = (activeOrderDrafts[draftKey] || '').trim();
    let val = parseFloat(draft);
    if (!Number.isFinite(val)) val = lg.ordered;
    return Math.round(lg.start - lg.consumed + val);
  }

  // ── Partial-month awareness ──────────────────────────────────────────────
  // A YM can be partially saved (e.g., starter saved but grower / layerfeed
  // missing). The active card must NOT overwrite the persisted value or
  // force the operator to retype it.
  //   • In auto-active mode (editingMonthYM == null), rows whose persisted
  //     value exists for activeYM render as plain text and pass through
  //     unchanged on save. Only the missing rows accept input.
  //   • In edit mode (operator clicked Edit on the pinned fully-saved month),
  //     all three rows become editable so the operator can change
  //     any of them. Drafts are pre-loaded with the persisted values.
  //   (isActiveEditMode is declared earlier so a pinned saved month can
  //   switch from read-only values into editable inputs.)
  function persistedValueForActive(orderKey: FeedOrderKey) {
    return (feedOrders[orderKey] || {})[activeYM];
  }
  function isRowPersistedForActive(orderKey: FeedOrderKey) {
    return persistedValueForActive(orderKey) != null;
  }

  // ── Session handlers ─────────────────────────────────────────────────────
  const ORDER_KEYS: FeedOrderKey[] = ['starter', 'grower', 'layerfeed'];
  const orderDoneRef = useRef(false);
  const [orderDoneBusy, setOrderDoneBusy] = useState(false);
  // Typing in a row opens the order session on the month it is typed for and
  // schedules that field's revision. A field's draft is scheduled only when
  // the operator typed it: persisted rows in non-edit mode stay locked plain
  // text and are never re-scheduled, and a blank is never a zero — nothing
  // writes from a recommendation or an unrelated row alone. One edited feed
  // saves by itself; it does not wait for its neighbours' blanks.
  function onOrderInput(orderKey: FeedOrderKey, value: string) {
    const ym = editingMonthYM ?? activeYM;
    if (editingMonthYM == null) setEditingMonthYM(ym);
    setActiveOrderDrafts((prev) => ({...prev, [orderKey]: value}));
    setOrderFailure((prev) => ({...prev, [orderKey]: null}));
    orderAutosaveByOrderKey[orderKey].controller.update({owner, ym, raw: value});
  }
  // Done drains the latest revisions and closes only when all three fields
  // settled clean, for the same owner and the same month. Anything typed
  // meanwhile joins the same drain; a refusal or failure keeps the session
  // and its drafts.
  async function doneOrderEdit(sessionYM?: string) {
    const ym = sessionYM ?? editingMonthYMRef.current;
    const startOwnerKey = ownerRef.current ? ownerRef.current.key : '';
    if (ym == null || orderDoneRef.current) return;
    orderDoneRef.current = true;
    setOrderDoneBusy(true);
    try {
      const settled = await Promise.all(ORDER_KEYS.map((k) => orderAutosaveByOrderKey[k].controller.flush()));
      if (!aliveRef.current) return;
      if (settled.some((s) => !s)) return;
      if (ORDER_KEYS.some((k) => orderAutosaveByOrderKey[k].controller.isDirty())) return;
      if ((ownerRef.current ? ownerRef.current.key : '') !== startOwnerKey) return;
      if (editingMonthYMRef.current !== ym) return;
      setEditingMonthYM(null);
      setActiveOrderDrafts({starter: '', grower: '', layerfeed: ''});
    } finally {
      orderDoneRef.current = false;
      if (aliveRef.current) setOrderDoneBusy(false);
    }
  }
  // "Accept 0", the recorded zero decision ("Save 0" before autosave): with
  // no drafts typed, no persisted row for the month, and a recommendation of
  // exactly 0 for all three types, the operator marks the month decided
  // without typing it. Explicit and never automatic: a blank input on its own
  // writes nothing, and each accepted 0 is that field's own typed-zero
  // revision with its own request id.
  function acceptZeroOrder() {
    const anyDraft = ORDER_KEYS.some((k) => (activeOrderDrafts[k] || '').trim() !== '');
    const anyPersisted = ORDER_KEYS.some((k) => persistedValueForActive(k) != null);
    if (anyDraft || anyPersisted) return;
    if (recommendedOrder.starter !== 0 || recommendedOrder.grower !== 0 || recommendedOrder.layer !== 0) return;
    const ym = editingMonthYM ?? activeYM;
    setEditingMonthYM(ym);
    setActiveOrderDrafts({starter: '0', grower: '0', layerfeed: '0'});
    ORDER_KEYS.forEach((k) => orderAutosaveByOrderKey[k].controller.update({owner, ym, raw: '0'}));
    void doneOrderEdit(ym);
  }
  function editMonth(ym: string) {
    setEditingMonthYM(ym);
    setActiveOrderDrafts({
      starter: (feedOrders.starter || {})[ym] != null ? String((feedOrders.starter || {})[ym]) : '',
      grower: (feedOrders.grower || {})[ym] != null ? String((feedOrders.grower || {})[ym]) : '',
      layerfeed: (feedOrders.layerfeed || {})[ym] != null ? String((feedOrders.layerfeed || {})[ym]) : '',
    });
  }

  // Draft/recommendation state behind the Accept 0 action. There is no Save
  // Order button any more: each typed field autosaves itself, and the only
  // explicit order action left is accepting a wholly fresh all-zero month.
  const drafts = activeOrderDrafts;
  const hasDraftStarter = (drafts.starter || '').trim() !== '';
  const hasDraftGrower = (drafts.grower || '').trim() !== '';
  const hasDraftLayer = (drafts.layerfeed || '').trim() !== '';
  const anyDraftHasValue = hasDraftStarter || hasDraftGrower || hasDraftLayer;
  const anyTypePersistedForActive =
    isRowPersistedForActive('starter') || isRowPersistedForActive('grower') || isRowPersistedForActive('layerfeed');
  // "Accept 0" only for a wholly fresh all-zero month. Any typed draft or
  // persisted row disqualifies it; each typed 0 autosaves on its own anyway.
  const allZeroPath =
    !anyDraftHasValue &&
    !anyTypePersistedForActive &&
    recommendedOrder.starter === 0 &&
    recommendedOrder.grower === 0 &&
    recommendedOrder.layer === 0;

  // ── Physical count inputs ────────────────────────────────────────────────
  // Physical count is "what is on site now" — the revision stamps the day it
  // was typed; no editable date input. BOTH INPUTS ARE SEEDED-THEN-OVERRIDDEN
  // RATHER THAN SEEDED IN useState: `null` means "has not typed", and the
  // persisted value shows until they do. Clearing the field stores '' rather
  // than null, so a deliberate blank stays blank.
  const [countLbsEdit, setCountLbsEdit] = useState<string | null>(null);
  const persistedCount = poultryFeedInventory && (poultryFeedInventory as PoultryFeedInventoryMap)[countType];
  const countLbsInput =
    countLbsEdit ?? (persistedCount && persistedCount.count != null ? String(persistedCount.count) : '');
  const [countIncludesEdit, setCountIncludesEdit] = useState<boolean | null>(null);
  const countIncludesInput = countIncludesEdit ?? !!(persistedCount && persistedCount.includesCurrentMonthDelivery);
  const [countNotice, setCountNotice] = useState<Notice | null>(null);
  const countMonthShort = (() => {
    const [y, m] = todayDate.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', {month: 'short'});
  })();

  // Each keystroke is a revision dated the day it was typed, under the feed
  // type selected when it was typed. The delivery toggle rides with a typed
  // count; on an untouched count it is held incomplete, because writing it
  // would re-date an older count to today. Toggled back to the confirmed
  // value it settles without a request.
  function onCountInput(value: string) {
    setCountLbsEdit(value);
    setCountFailure(null);
    countAutosave.controller.update({
      owner,
      type: countType,
      raw: value,
      typed: true,
      includes: countIncludesInput,
      date: todayDate,
    });
  }
  function onCountIncludesToggle(checked: boolean) {
    setCountIncludesEdit(checked);
    setCountFailure(null);
    countAutosave.controller.update({
      owner,
      type: countType,
      raw: countLbsEdit ?? '',
      typed: countLbsEdit !== null,
      includes: checked,
      date: todayDate,
    });
  }
  // Switching feed type first drains the open revision: a valid draft settles
  // and the switch proceeds; an invalid one stays on its type with a truthful
  // notice instead of being silently dropped or written half-typed.
  async function switchCountType(next: string) {
    if (next === countType) return;
    if (countAutosave.controller.isDirty()) {
      const settled = await countAutosave.controller.flush();
      if (!settled || countAutosave.controller.isDirty()) {
        setCountNotice({kind: 'error', message: 'Fix the current count before switching feed type.'});
        return;
      }
    }
    setCountType(next);
    setCountLbsEdit(null);
    setCountIncludesEdit(null);
    setCountFailure(null);
    setCountNotice(null);
  }

  // A new owner never inherits the old owner's text or open session. Reset
  // during render, so not even its first frame shows them; the controllers
  // are per owner already, and a stale owner's ack/failure never paints.
  const [draftOwnerKey, setDraftOwnerKey] = useState(ownerKey);
  if (draftOwnerKey !== ownerKey) {
    setDraftOwnerKey(ownerKey);
    setEditingMonthYM(null);
    setActiveOrderDrafts({starter: '', grower: '', layerfeed: ''});
    setCountLbsEdit(null);
    setCountIncludesEdit(null);
    setCountNotice(null);
  }

  // What the count form says under the input: the reason a kept entry is not
  // being written, or the current owner's failure. The header chip says the
  // rest.
  const countProblem = (() => {
    if (countFailure && countFailure.ownerKey === ownerKey) return countFailure.message;
    const draft: BroilerCountDraft = {
      owner,
      type: countType,
      raw: countLbsEdit ?? '',
      typed: countLbsEdit !== null,
      includes: countIncludesInput,
      date: todayDate,
    };
    return broilerCountDraftProblem(draft, owner, confirmedIncludesFor(countType));
  })();
  // What each order row says under its input: that field's current-owner
  // failure first, then why its typed draft is not being written. Persisted
  // locked rows and untouched blanks say nothing.
  const rowProblemFor = (orderKey: FeedOrderKey): string | null => {
    const fail = orderFailure[orderKey];
    if (fail && fail.ownerKey === ownerKey) return fail.message;
    const raw = activeOrderDrafts[orderKey] || '';
    if (raw.trim() === '') return null;
    return broilerOrderDraftProblem({owner, ym: activeYM, raw}, owner);
  };

  // ── Monthly card renderer ────────────────────────────────────────────────
  function renderMonthCard(ym: string, readOnly = false) {
    const md = monthlyData.find((m) => m.ym === ym);
    if (!md) return null;
    const isActive = ym === activeYM && !readOnly;
    const isActiveSavedCard = isActive && isMonthFullySaved(ym) && !isActiveEditMode;
    // WI-5: ledger card uses a standard 1px gray border (no green 2px accent) and
    // a plain white header (no green tint). Active state is shown by the pill.
    const cardBorder = '1px solid var(--border)';
    const cardHeaderBg = 'white';

    // WI-2e: feed-type words are categories, not colors → black labels.
    const rowDefs: {key: FeedTypeKey; label: string; orderKey: FeedOrderKey; draftKey: FeedOrderKey}[] = [
      {key: 'starter', label: 'Starter', orderKey: 'starter', draftKey: 'starter'},
      {key: 'grower', label: 'Grower', orderKey: 'grower', draftKey: 'grower'},
      {key: 'layer', label: 'Layer Feed', orderKey: 'layerfeed', draftKey: 'layerfeed'},
    ];

    return (
      <div
        key={ym}
        style={{
          background: 'white',
          border: cardBorder,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: cardHeaderBg,
          }}
        >
          <span style={{fontSize: 14, fontWeight: 700, color: 'var(--ink)'}}>{ymLabel(ym)}</span>
          {isActive && (
            <Badge variant="ok" style={{borderRadius: 10}}>
              ACTIVE
            </Badge>
          )}
        </div>

        {/* Per-type equation rows */}
        <div style={{padding: '4px 16px 8px'}}>
          {rowDefs.map((row) => {
            const lg = pLedger[row.key][ym];
            const savedVal = (feedOrders[row.orderKey] || {})[ym];
            const isSaved = savedVal != null && savedVal !== '';
            const draft = activeOrderDrafts[row.draftKey];
            const recVal = recommendedOrder[row.key];
            const recText = recVal != null ? `${recVal.toLocaleString()} lbs` : '—';
            // In auto-active mode, a row whose value is already persisted
            // renders as plain text (no input) and the operator's save
            // carries it through unchanged. In edit mode (operator clicked
            // Edit on a fully-saved month) all three rows are editable.
            const rowShowsInput = isActive && (isActiveEditMode || !isSaved);
            const liveEnd = isActive && lg ? activeDraftEnd(row.key) : lg ? lg.end : null;
            return (
              <div
                key={row.key}
                data-mobile-stack-equation="1"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '90px 1fr 14px 1fr 14px 1fr 14px 1fr',
                  gap: 8,
                  alignItems: 'end',
                  padding: '6px 0',
                  borderTop: '1px solid var(--divider)',
                }}
              >
                <div style={{fontSize: 12, fontWeight: 700, color: 'var(--text-primary)'}}>{row.label}</div>
                <div style={{textAlign: 'right'}}>
                  <div style={{fontSize: 9, color: 'var(--ink-faint)', letterSpacing: 0.5}}>Start</div>
                  <div
                    style={{fontSize: 13, fontWeight: 600, color: lg && lg.start >= 0 ? 'var(--ink)' : 'var(--danger)'}}
                  >
                    {lg ? lg.start.toLocaleString() : '—'}
                  </div>
                </div>
                <div style={{fontSize: 20, fontWeight: 300, color: 'var(--operator)', textAlign: 'center'}}>{'−'}</div>
                <div style={{textAlign: 'right'}}>
                  <div style={{fontSize: 9, color: 'var(--ink-faint)', letterSpacing: 0.5}}>Consumed</div>
                  <div style={{fontSize: 13, fontWeight: 600, color: 'var(--ink)'}}>
                    {lg ? lg.consumed.toLocaleString() : '—'}
                  </div>
                </div>
                <div style={{fontSize: 20, fontWeight: 300, color: 'var(--operator)', textAlign: 'center'}}>{'+'}</div>
                {/* Ordered cell: label sits directly above the value/input
                    (both right-aligned) so saved values like 4,000 read as
                    being IN the Ordered column rather than floating. */}
                <div style={{textAlign: 'right'}}>
                  <div
                    style={{
                      fontSize: 9,
                      color: 'var(--ink-faint)',

                      letterSpacing: 0.5,
                      marginBottom: 2,
                    }}
                  >
                    Ordered
                  </div>
                  {rowShowsInput ? (
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        data-feed-suggested-order={row.key}
                        style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600, whiteSpace: 'nowrap'}}
                      >
                        {'Suggested: ' + recText}
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="100"
                        value={draft}
                        onChange={(e) => onOrderInput(row.draftKey, e.target.value)}
                        style={{
                          width: 80,
                          fontSize: 13,
                          padding: '4px 8px',
                          border: '1px solid var(--border-strong)',
                          borderRadius: 10,
                          textAlign: 'right',
                          fontFamily: 'inherit',
                          fontWeight: 600,
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                      }}
                    >
                      {isActive && (
                        <span
                          data-feed-suggested-order={row.key}
                          style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600, whiteSpace: 'nowrap'}}
                        >
                          {'Suggested: ' + recText}
                        </span>
                      )}
                      <div style={{fontSize: 13, fontWeight: 600, color: 'var(--ink)'}}>
                        {isSaved ? Number(savedVal).toLocaleString() : '—'}
                      </div>
                    </div>
                  )}
                  {rowProblemFor(row.draftKey) ? (
                    <div
                      data-feed-order-problem={row.draftKey}
                      style={{fontSize: 10, color: 'var(--danger)', marginTop: 2, textAlign: 'right'}}
                    >
                      {rowProblemFor(row.draftKey)}
                    </div>
                  ) : null}
                </div>
                <div style={{fontSize: 20, fontWeight: 300, color: 'var(--operator)', textAlign: 'center'}}>{'='}</div>
                <div style={{textAlign: 'right'}}>
                  <div style={{fontSize: 9, color: 'var(--ink-faint)', letterSpacing: 0.5}}>End</div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: liveEnd != null ? (liveEnd > 0 ? 'var(--ok-ink)' : 'var(--danger)') : 'var(--ink-faint)',
                    }}
                  >
                    {liveEnd != null ? liveEnd.toLocaleString() : '—'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Active-card save row; fully saved pinned month exposes Edit. */}
        {isActiveSavedCard ? (
          <div
            style={{
              padding: '8px 16px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid var(--divider)',
            }}
          >
            <div data-feed-order-basis-caption="poultry" style={{fontSize: 11, color: 'var(--ink-muted)'}}>
              {orderBasisCaption}
            </div>
            <Button type="button" onClick={() => editMonth(ym)}>
              Edit
            </Button>
          </div>
        ) : isActive ? (
          <div
            style={{
              padding: '8px 16px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid var(--divider)',
            }}
          >
            <div data-feed-order-basis-caption="poultry" style={{fontSize: 11, color: 'var(--ink-muted)'}}>
              {orderBasisCaption}
            </div>
            <div style={{display: 'flex', gap: 8}}>
              {allZeroPath && (
                <Button variant="primary" type="button" onClick={acceptZeroOrder}>
                  Accept 0
                </Button>
              )}
              {isActiveEditMode && (
                <Button type="button" onClick={() => void doneOrderEdit(ym)} disabled={orderDoneBusy}>
                  Done
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // Three states where there used to be one. Every value on this page arrived
  // with the boot payload, so "loaded" was the only frame that could reach the
  // ledger and the gate that produced it was App's, not this view's. A
  // route-local read makes pending and failed real, and neither may render as a
  // ledger: the Save button writes `{...feedOrders, …}` back to app_store, so a
  // ledger drawn over a not-yet-loaded blob would persist a zeroed month over
  // real orders on one click. That is why this is an early return rather than
  // an in-page notice — and it sits below every hook so the hook order is
  // identical in all three states.
  if (!feedData) {
    return (
      <div>
        <Header />
        <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
          {feedResource.loading ? (
            <div
              data-poultry-feed-loading
              style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
            >
              Loading feed ledger…
            </div>
          ) : (
            // Boolean-free branch: `error` is `unknown` on the resource
            // contract, and the only way to be here without data and without
            // loading is a failed read.
            <div
              data-poultry-feed-load-error
              style={{textAlign: 'center', padding: '3rem', color: 'var(--danger)', fontSize: 13}}
            >
              Could not load the poultry feed ledger.{' '}
              <Button type="button" onClick={() => feedResource.refetch()}>
                Retry
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const lastCountDate = TYPE_KEYS.map((type) => poultryFeedInventory[type]?.date ?? '')
    .sort()
    .at(-1);
  const activeBrFeed = activeBroilers.filter((b) => calcPoultryStatus(b) === 'active');
  const plannedBrFeed = activeBroilers.filter((b) => calcPoultryStatus(b) === 'planned');
  const processedBrFeed = batches
    .filter((b) => calcPoultryStatus(b) === 'processed' && b.hatchDate)
    .sort((a, b) => (b.processingDate || b.hatchDate || '').localeCompare(a.processingDate || a.hatchDate || ''));
  const countSaving = countAutosave.state === 'pending' || countAutosave.state === 'saving';
  const countUnsaved = Boolean(countProblem) || countAutosave.state === 'invalid' || countAutosave.state === 'failed';

  function feedComparison(actual: number, planned: number) {
    const remaining = Math.round(planned - actual);
    return (
      <div className="pfr-feed-value">
        <div>
          <strong>{actual > 0 ? Math.round(actual).toLocaleString() : '—'}</strong>
          <span> / {Math.round(planned).toLocaleString()}</span>
        </div>
        <small className={remaining < 0 ? 'pfr-over' : undefined}>
          {Math.abs(remaining).toLocaleString()} {remaining < 0 ? 'over' : 'left'}
        </small>
      </div>
    );
  }

  function renderBatchRows(rows: BroilerBatchRecord[]) {
    return rows.map((batch) => {
      const planned = calcBatchFeed(batch);
      const actual = calcBroilerStatsFromDailys(batch, broilerDailys);
      const status = calcPoultryStatus(batch);
      const end = status === 'processed' ? batch.processingDate : todayDate;
      const days =
        batch.hatchDate && end
          ? Math.max(
              0,
              Math.round(
                (new Date(end + 'T12:00:00').getTime() - new Date(batch.hatchDate + 'T12:00:00').getTime()) / 86400000,
              ),
            )
          : null;
      return (
        <div
          key={batch.id}
          className="pfr-batch-row hoverable-tile"
          {...openableProps(() => navigate('/broiler/batches/' + encodeURIComponent(batch.name)))}
          aria-label={'Open batch ' + batch.name}
        >
          <div className="pfr-row-name">
            <span className="pfr-group-dot" style={{background: getBatchDisplayColor(batch).bg}} />
            <strong title={batch.name}>{batch.name}</strong>
          </div>
          <div className="pfr-breed">
            <strong>{breedLabel(batch.breed)}</strong>
            <span>
              {' '}
              ·{' '}
              {status === 'active' && days !== null && days < 14
                ? 'Brooder ' + (batch.brooder ?? '—')
                : 'Schooner ' + (batch.schooner ?? '—')}
            </span>
          </div>
          <div className="pfr-age">{days === null ? '—' : Math.floor(days / 7) + ' wk ' + (days % 7) + ' d'}</div>
          {feedComparison(actual.starterFeed, planned.starter)}
          {feedComparison(actual.growerFeed, planned.grower)}
          {feedComparison(actual.starterFeed + actual.growerFeed, planned.total)}
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </div>
      );
    });
  }

  return (
    <div>
      <Header />
      <main className="poultry-feed-redesign">
        <div className="pfr-title-row">
          <h1 className="poultry-page-title">Poultry Feed</h1>
          <span>
            {today.toLocaleDateString('en-US', {month: 'long', year: 'numeric'})}
            {lastCountDate ? ' · Last count ' + fmt(lastCountDate) : ''}
          </span>
        </div>
        <section
          className="pfr-summary-card"
          data-feed-order-tile="poultry-thru-next-delta"
          aria-label="Poultry feed balance"
        >
          <div className="pfr-phone-order-actions">
            <strong>{'Ordered · ' + activeLabel + ' · Thru ' + nextLabel}</strong>
            {isMonthFullySaved(activeYM) && !isActiveEditMode ? (
              <Button type="button" onClick={() => editMonth(activeYM)}>
                Edit Order
              </Button>
            ) : null}
            {isActiveEditMode ? (
              <Button type="button" onClick={() => void doneOrderEdit(activeYM)} disabled={orderDoneBusy}>
                {orderDoneBusy ? 'Saving…' : 'Done'}
              </Button>
            ) : null}
          </div>
          <div className="pfr-equation-scroll">
            <div className="poultry-feed-equation" role="table" aria-label="Feed on hand, ordered and needed by type">
              <div className="pfe-head" role="row">
                <span role="columnheader" />
                <span role="columnheader">On Hand</span>
                <span aria-hidden="true" />
                <span role="columnheader" className="pfr-ordered-heading" data-feed-order-tile="poultry-order">
                  {'Ordered · ' + activeLabel}
                  {isMonthFullySaved(activeYM) && !isActiveEditMode ? (
                    <button
                      type="button"
                      className="pfe-edit wcf-clickable"
                      data-poultry-edit-order="1"
                      onClick={() => editMonth(activeYM)}
                    >
                      Edit Order
                    </button>
                  ) : null}
                  {isActiveEditMode ? (
                    <button
                      type="button"
                      className="pfe-edit wcf-clickable"
                      onClick={() => void doneOrderEdit(activeYM)}
                      disabled={orderDoneBusy}
                    >
                      {orderDoneBusy ? 'Saving…' : 'Done'}
                    </button>
                  ) : null}
                </span>
                <span aria-hidden="true" />
                <span role="columnheader">{'Need Thru ' + nextLabel}</span>
                <span aria-hidden="true" />
                <span role="columnheader">{'Thru ' + nextLabel}</span>
              </div>
              {TILE_TYPE_ROWS.map((row) => {
                const orderKey: FeedOrderKey = row.key === 'layer' ? 'layerfeed' : (row.key as FeedOrderKey);
                const basis = orderBasisByType[row.key];
                const need = needThruNext[row.key];
                const delta = thruNextDeltaByType[row.key];
                const savedOrder = persistedValueForActive(orderKey);
                const suggestedOrder = recommendedOrder[row.key];
                const showInput = isActiveEditMode || !isRowPersistedForActive(orderKey);
                const orderIncluded = orderIncludedByType[row.key];
                const problem = rowProblemFor(orderKey);
                return (
                  <div className="pfe-row" role="row" key={row.key} data-feed-order-row={row.key}>
                    <span className="pfe-type" role="cell">
                      {row.label}
                    </span>
                    <span className="pfe-num" role="cell" data-feed-equation-cell="onHand" title={orderBasisCaption}>
                      {basis == null ? (
                        '—'
                      ) : (
                        <>
                          {basis.toLocaleString()} <small>lb</small>
                        </>
                      )}
                    </span>
                    <span className="pfe-op" aria-hidden="true">
                      +
                    </span>
                    <span className="pfe-num pfr-ordered" role="cell" data-feed-equation-cell="ordered">
                      {orderIncluded ? (
                        <>
                          0 <small>lb</small>
                          <small className="pfr-order-included">
                            {activeOrderedByType[row.key].toLocaleString()} lb already in On Hand
                          </small>
                        </>
                      ) : null}
                      {showInput ? (
                        <div className="pfr-order-input-wrap">
                          {orderIncluded ? <small>Order total</small> : null}
                          <div className="pfr-unit-input">
                            <input
                              type="number"
                              min="0"
                              step="100"
                              aria-label={row.label + ' order for ' + activeLabel}
                              value={activeOrderDrafts[orderKey]}
                              placeholder="—"
                              onChange={(e) => onOrderInput(orderKey, e.target.value)}
                            />
                            <span>lb</span>
                          </div>
                          <small data-feed-suggested-order={row.key}>
                            {suggestedOrder == null ? '—' : 'Suggested ' + suggestedOrder.toLocaleString() + ' lb'}
                          </small>
                        </div>
                      ) : orderIncluded ? null : savedOrder == null ? (
                        '—'
                      ) : (
                        <>
                          {activeOrderedByType[row.key].toLocaleString()} <small>lb</small>
                        </>
                      )}
                      {problem ? (
                        <span className="pfr-field-error" data-feed-order-problem={orderKey}>
                          {problem}
                          <button
                            type="button"
                            onClick={() => void orderAutosaveByOrderKey[orderKey].controller.flush()}
                          >
                            Retry
                          </button>
                        </span>
                      ) : null}
                    </span>
                    <span className="pfe-op" aria-hidden="true">
                      −
                    </span>
                    <span className="pfe-num" role="cell" data-feed-equation-cell="need">
                      {need.toLocaleString()} <small>lb</small>
                    </span>
                    <span className="pfe-op" aria-hidden="true">
                      =
                    </span>
                    <span
                      className="pfe-num pfe-result"
                      role="cell"
                      data-feed-equation-cell="delta"
                      data-result={delta == null ? 'unknown' : delta < 0 ? 'short' : 'covered'}
                    >
                      {delta == null ? (
                        '—'
                      ) : (
                        <>
                          {delta > 0 ? '+' : ''}
                          {delta.toLocaleString()} <small>lb</small>
                        </>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          {allZeroPath ? (
            <div className="pfr-zero-order">
              <Button variant="primary" type="button" onClick={acceptZeroOrder}>
                Accept 0
              </Button>
            </div>
          ) : null}
          <div className="pfr-count-well">
            <strong>Update On Hand</strong>
            <div className="pfr-count-types" role="group" aria-label="Feed type" id="poultry-feed-count-type">
              {TILE_TYPE_ROWS.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  aria-pressed={countType === row.key}
                  onClick={() => void switchCountType(row.key)}
                >
                  {row.label}
                </button>
              ))}
            </div>
            <div className="pfr-unit-input pfr-count-input">
              <input
                id="poultry-feed-count-input"
                type="number"
                min="0"
                step="100"
                aria-label="Pounds on hand"
                placeholder="—"
                value={countLbsInput}
                onChange={(e) => onCountInput(e.target.value)}
              />
              <span>lb</span>
            </div>
            <label className="pfr-includes-switch">
              <input
                id="poultry-feed-count-includes-delivery"
                type="checkbox"
                role="switch"
                checked={countIncludesInput}
                onChange={(e) => onCountIncludesToggle(e.target.checked)}
              />
              <span className="pfr-switch-track" aria-hidden="true" />
              <span>{'Includes ' + countMonthShort + ' order'}</span>
            </label>
            <span
              className={'pfr-count-status' + (countUnsaved ? ' pfr-count-status--error' : '')}
              role="status"
              aria-live="polite"
            >
              {countSaving
                ? 'Saving…'
                : countUnsaved
                  ? 'Not saved'
                  : persistedCount
                    ? '✓ Saved · ' + fmt(persistedCount.date)
                    : 'No count yet'}
            </span>
          </div>
          {countNotice || countProblem ? (
            <div className="pfr-notice">
              <InlineNotice
                notice={countNotice ?? {kind: 'error', message: countProblem as string}}
                onDismiss={() => setCountNotice(null)}
                action={
                  countAutosave.state === 'failed' ? (
                    <Button type="button" onClick={() => void countAutosave.controller.flush()}>
                      Retry
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : null}
        </section>
        <section aria-labelledby="pfr-batches-heading">
          <h2 className="pfr-section-title" id="pfr-batches-heading">
            Feed by Batch <span>{activeBrFeed.length} active</span>
          </h2>
          <div className="pfr-list-card">
            <div className="pfr-batch-row pfr-table-head" aria-hidden="true">
              <span>Batch</span>
              <span>Breed · Housing</span>
              <span>Age</span>
              <span>Starter</span>
              <span>Grower</span>
              <span>Total</span>
              <span />
            </div>
            {activeBrFeed.length ? (
              renderBatchRows(activeBrFeed)
            ) : (
              <p className="pfr-empty">No active broiler batches.</p>
            )}
            {plannedBrFeed.length ? (
              <>
                <div className="pfr-table-subheading">Planned</div>
                {renderBatchRows(plannedBrFeed)}
              </>
            ) : null}
            <div className="pfr-batch-row pfr-total-row">
              <strong>Total</strong>
              <span />
              <span />
              {feedComparison(
                activeBrFeed.reduce((sum, b) => sum + calcBroilerStatsFromDailys(b, broilerDailys).starterFeed, 0),
                activeBrFeed.reduce((sum, b) => sum + calcBatchFeed(b).starter, 0),
              )}
              {feedComparison(
                activeBrFeed.reduce((sum, b) => sum + calcBroilerStatsFromDailys(b, broilerDailys).growerFeed, 0),
                activeBrFeed.reduce((sum, b) => sum + calcBatchFeed(b).grower, 0),
              )}
              {feedComparison(
                activeBrFeed.reduce((sum, b) => {
                  const actual = calcBroilerStatsFromDailys(b, broilerDailys);
                  return sum + actual.starterFeed + actual.growerFeed;
                }, 0),
                activeBrFeed.reduce((sum, b) => sum + calcBatchFeed(b).total, 0),
              )}
              <span />
            </div>
            {processedBrFeed.length ? (
              <>
                <button
                  type="button"
                  className="pfr-completed-toggle"
                  aria-expanded={showCompleted}
                  aria-controls="pfr-completed-batches"
                  onClick={() => setShowCompleted((open) => !open)}
                >
                  {processedBrFeed.length} completed <span>· {showCompleted ? 'Hide' : 'Show'}</span>
                </button>
                <div id="pfr-completed-batches" hidden={!showCompleted}>
                  {showCompleted ? renderBatchRows(processedBrFeed) : null}
                </div>
              </>
            ) : null}
          </div>
        </section>
        <section aria-labelledby="pfr-flocks-heading">
          <h2 className="pfr-section-title" id="pfr-flocks-heading">
            Feed by Flock <span>{activeLayerBatchesForFeed.length} active</span>
          </h2>
          <div className="pfr-list-card">
            <div className="pfr-flock-row pfr-table-head" aria-hidden="true">
              <span>Flock</span>
              <span>Housing</span>
              <span>Hens</span>
              <span>Phase</span>
              <span>Starter</span>
              <span>Grower</span>
              <span>Layer / Year</span>
              <span />
            </div>
            {activeLayerBatchesForFeed.length === 0 ? (
              <p className="pfr-empty">No active layer flocks.</p>
            ) : (
              activeLayerBatchesForFeed.map((batch) => {
                const startDate = batch.brooder_entry_date || batch.arrival_date;
                const birdCount = parseInt(String(batch.original_count)) || 0;
                const housings = activeHousings.filter((housing) => housing.batch_id === batch.id);
                let hens = housings.reduce((sum, housing) => {
                  const projected = computeProjectedCount(housing, allLayerDailys, layerHousings);
                  return sum + (projected ? (projected.projected ?? 0) : parseInt(String(housing.current_count)) || 0);
                }, 0);
                if (hens === 0) hens = birdCount;
                let starter = 0;
                let grower = 0;
                LAYER_FEED_SCHEDULE.forEach((week) => {
                  if (week.phase === 'starter') starter += week.lbsPerBird * birdCount;
                  else grower += week.lbsPerBird * birdCount;
                });
                starter = Math.min(starter, 1500);
                const ageDays = startDate
                  ? Math.max(0, Math.floor((today.getTime() - new Date(startDate + 'T12:00:00').getTime()) / 86400000))
                  : null;
                const phase = ageDays === null ? '—' : ageDays < 42 ? 'Starter' : ageDays < 140 ? 'Grower' : 'Layer';
                return (
                  <div
                    key={batch.id}
                    className="pfr-flock-row hoverable-tile"
                    {...openableProps(() => navigate('/layer/batches/' + encodeURIComponent(String(batch.id))))}
                    aria-label={'Open flock ' + (batch.name ?? batch.id)}
                  >
                    <div className="pfr-row-name">
                      <span className="pfr-group-dot" style={{background: getBatchDisplayColor(batch).bg}} />
                      <strong title={batch.name ?? undefined}>{batch.name || '—'}</strong>
                    </div>
                    <span className="pfr-housing">
                      {housings
                        .map((housing) => String(housing.name ?? housing.housing_name ?? ''))
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </span>
                    <span>{hens.toLocaleString()}</span>
                    <span className="pfr-phase">{phase}</span>
                    <strong className="pfr-flock-number">{Math.round(starter).toLocaleString()}</strong>
                    <strong className="pfr-flock-number">{Math.round(grower).toLocaleString()}</strong>
                    <strong className="pfr-flock-number">
                      {Math.round(hens * LAYER_FEED_PER_DAY * 365).toLocaleString()}
                    </strong>
                    <span className="chev" aria-hidden="true">
                      ›
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </section>
        <Collapsible label="Monthly Feed Ledger" defaultOpen={false} stateKey="broiler.feed.ledger">
          {renderMonthCard(activeYM, true)}
        </Collapsible>
      </main>
    </div>
  );
}
